import { consentDocument, gateDefinition, type ConsentGate } from '@toothsomatics/consent-catalog';

import './consent-checklist.css';

/**
 * The acknowledgements for one moment in the journey.
 *
 * **One gate, one checkbox, one record.** Never a single tick covering two ideas — bundling is
 * exactly the pattern regulators penalise, and it makes it impossible to say afterwards which of
 * the two a person actually agreed to, which is the only question anyone ever asks. If a moment
 * needs four acknowledgements, four boxes appear here.
 *
 * The words come from `packages/consent-catalog`, which the API also reads. This screen cannot show
 * one text while the server records another, and neither can drift from the other in a release.
 *
 * **Nothing here decides anything.** The server resolves the active document version itself,
 * verifies its checksum against what the client echoes back, writes its own record with its own
 * clock, and refuses the operation with 409 CONSENT_REQUIRED when a record is missing or stale.
 * Ticking every box in this component and then editing the request in a browser gets you a 409.
 */

export interface ConsentChecklistProps {
  readonly heading: string;
  readonly intro?: string;
  readonly gates: readonly ConsentGate[];
  readonly accepted: Readonly<Partial<Record<ConsentGate, boolean>>>;
  readonly onChange: (gate: ConsentGate, value: boolean) => void;
}

export function ConsentChecklist({
  heading,
  intro,
  gates,
  accepted,
  onChange,
}: ConsentChecklistProps) {
  return (
    <section className="consents" aria-labelledby="consents-heading">
      <h2 className="consents__heading" id="consents-heading">
        {heading}
      </h2>
      {intro ? <p className="consents__intro">{intro}</p> : null}

      {gates.map((gate) => {
        const terms = consentDocument(gate);
        const definition = gateDefinition(gate);
        const isAccepted = accepted[gate] === true;

        return (
          <div className="consent" key={gate}>
            {/*
              Never pre-checked, and `required` is not put on the input: a browser refusing to
              submit is not consent enforcement, it is a hint. The refusal that counts is the
              server's.
            */}
            <label className="consent__tick">
              <input
                type="checkbox"
                checked={isAccepted}
                onChange={(event) => onChange(gate, event.target.checked)}
              />
              <span className="consent__words">
                {terms.acknowledgement}
                {definition.required ? null : (
                  <span className="consent__optional"> — optional, and you may decline</span>
                )}
              </span>
            </label>

            {/* The full text, always reachable. Never a link away, never summarised. */}
            <details className="consent__terms">
              <summary>Read this in full</summary>
              {terms.body.split('\n\n').map((paragraph) => (
                <p key={paragraph.slice(0, 24)}>{paragraph}</p>
              ))}
            </details>
          </div>
        );
      })}
    </section>
  );
}

/** Whether every required gate in a set has been ticked. Presentation only — the API re-decides. */
export function allRequiredAccepted(
  gates: readonly ConsentGate[],
  accepted: Readonly<Partial<Record<ConsentGate, boolean>>>,
): boolean {
  return gates.every((gate) => !gateDefinition(gate).required || accepted[gate] === true);
}
